import { useEffect, useRef, useState } from 'react'

/**
 * Smoothly scroll to an element by id, accounting for fixed headers via offset.
 * Works for window or the nearest scrollable ancestor.
 * @param {string} elmID - Target DOM id to scroll into view
 * @param {number} offset - Pixel offset to keep element below sticky headers
 */
function scrollToWithOffset (elmID, offset = 0) {
  const elm = document.getElementById(elmID)
  if (!elm) return

  // Find nearest scrollable ancestor; fall back to window if none is scrollable
  const getScrollableAncestor = (el) => {
    let parent = el?.parentElement
    while (parent) {
      const style = window.getComputedStyle(parent)
      const overflowY = style.overflowY
      const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight
      if (canScroll) return parent
      parent = parent.parentElement
    }
    return window
  }

  const container = getScrollableAncestor(elm)

  // Scroll the window; otherwise scroll the found container
  if (container === window) {
    const y = elm.getBoundingClientRect().top + window.pageYOffset - offset
    window.scrollTo({ top: y, behavior: 'smooth' })
  } else {
    const containerRect = container.getBoundingClientRect()
    const elmRect = elm.getBoundingClientRect()
    const targetTop = elmRect.top - containerRect.top - offset
    container.scrollTo({ top: targetTop, behavior: 'smooth' })
  }
}

/**
 * Custom hook to handle initial scroll behavior in forms
 * @param {Object} options - Options object
 * @param {string} options.initialScrollId - ID of the element to scroll to on initial load
 * @param {Object} options.elementsMap - Map of form elements
 * @param {Function} options.setStep - Function to set the current form step
 * @param {string} options.formType - Type of the form
 * @returns {void}
 */
export const useInitialScroll = ({ initialScrollId, elementsMap, setStep, formType }) => {
  // Ensure we only auto-scroll once per formType change
  const isFirstScroll = useRef(true)

  // Reset the 'first scroll' flag whenever the form type changes
  useEffect(() => {
    isFirstScroll.current = true
  }, [formType])

  // Track last measured top position to detect stabilization before scrolling
  const [scrollingTop, setScrollingTop] = useState(0)
  const previousScrollingTop = useRef(scrollingTop)

  // On first eligible render, navigate to the section and smooth-scroll to its anchor
  useEffect(() => {
    if (initialScrollId && isFirstScroll.current) {
      // Find the config element that declares it should be scrolled to on start
      const initialScrollElement = Object.values(elementsMap).find(
        (element) => element.elementType === 'scrollToOnStart' &&
        element.typeProps?.scrollToOnStartId === initialScrollId &&
        (formType === 'editable' || formType === 'printable')
      )
      if (initialScrollElement) {
        setStep(initialScrollElement.sectionIndex)
        const initialScrollDomElement = document.getElementById(initialScrollId)
        if (initialScrollDomElement) {
          // Delay to allow layout/render to stabilize so measurements are accurate
          setTimeout(() => {
            const initialScrollTop = Math.round(initialScrollDomElement.getBoundingClientRect().top)
            setScrollingTop(initialScrollTop)
            if (initialScrollTop !== previousScrollingTop.current) {
              previousScrollingTop.current = initialScrollTop
            } else {
              isFirstScroll.current = false
              // Offset accounts for sticky headers/toolbars height
              scrollToWithOffset(initialScrollId, 110)
            }
          }, 400)
        }
      }
    }
  }, [initialScrollId, elementsMap, setStep, formType, scrollingTop])
}
